import { Link } from 'react-router-dom'
import { TableShell } from '../../components/molecules/TableShell'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { Paginator } from '../../components/molecules/Paginator'
import { ROUTES, fillPath } from '../../../constants/routes.constants'
import { SCREENCODES } from '../../../constants/screen.constants'
import { useAuth } from '../../../context/AuthContext'
import {
  useNotificationTemplates,
  type NotificationTemplateDto
} from '../../../viewmodels/useNotificationTemplates'

/**
 * /dashboard/preferences/email-templates — list of editable email templates.
 *
 * Locked 2026-05-14 (Kiran). System-managed catalogue, one row per
 * notification kind. Admin clicks Edit to open the dedicated edit
 * page (separate route, NOT a modal — body fields are tall and a
 * side-by-side preview needs full-page real estate).
 */
export function NotificationTemplatesPage(): JSX.Element {
  const { items, total, page, pageSize, isLoading, error, setPage, reload } =
    useNotificationTemplates()

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-heading text-h2 text-charcoal">Email Templates</h1>
      </header>

      {error !== null ? (
        <Alert tone="error">
          {error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => void reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <Table items={items} />
          <Paginator
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            disabled={isLoading}
          />
        </>
      )}
    </div>
  )
}

function EmptyState(): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-heading text-h3 text-charcoal">No templates seeded</p>
      <p className="font-body text-body-sm text-text-secondary mt-2">
        Run migration 0030 to seed the verification template, then reload.
      </p>
    </div>
  )
}

function Table({ items }: { readonly items: NotificationTemplateDto[] }): JSX.Element {
  const { can } = useAuth()
  const canEdit = can(SCREENCODES.PREFERENCESEMAILTEMPLATES, 'edit')
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Name</Th>
            <Th>Code</Th>
            <Th>Subject</Th>
            <Th>Last updated</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((t) => (
            <tr key={t.code} className="border-t border-border">
              <Td>
                <span className="font-body text-body-sm text-charcoal">{t.name}</span>
              </Td>
              <Td>
                <span className="font-mono text-body-xs text-text-secondary">{t.code}</span>
              </Td>
              <Td>
                <span className="font-body text-body-sm text-text-primary truncate max-w-[36ch] block">
                  {t.subject}
                </span>
              </Td>
              <Td>
                <span className="font-body text-body-xs text-text-secondary">
                  {new Date(t.updated_at).toLocaleString()}
                </span>
              </Td>
              <Td className="text-right whitespace-nowrap">
                {canEdit ? (
                  <Link
                    to={fillPath(ROUTES.PREFERENCESEMAILTEMPLATEEDIT, { code: t.code })}
                  >
                    <Button size="sm" variant="secondary">
                      Edit
                    </Button>
                  </Link>
                ) : (
                  <span className="text-text-secondary">—</span>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableShell>
  )
}
